import { useState, useEffect } from 'react';
import { Grid, Button, Theme, FormControlLabel, RadioGroup, CircularProgress, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import VerifyDialog from '../../../components/common/VerifyDialog/VerifyDialog';
import GVTypography from 'components/lib/GVTypography/GVTypography';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';
import { CustomStatus, useInspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { useSelector } from 'react-redux';
import { getInspectionId } from 'store';
import GVRadio from 'components/lib/GVRadio/GVRadio';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import GVChip from 'components/GVChip/GVChip';
import { useTracker } from '../../Tracker/TrackerProvider';
import { FALSE_POSITIVE_COMMENT } from './constants';

type BulkActionModalProps = {
  open: boolean;
  onClose: () => void;
  onApply: (params: {
    statusId: string;
    comment: string;
    isFalsePositive: boolean;
    selectedStatusTitle?: string;
  }) => void;
  onApplyOverrides?: (
    params: { statusId: string; comment: string; isFalsePositive: boolean; selectedStatusTitle?: string },
    overrides: { overrideStatus: boolean; overrideComment: boolean },
  ) => void;
  onCancelOverrides?: () => void;
  selectedCount?: number;
  overrides?: {
    overrideStatus: boolean;
    overrideComment: boolean;
  };
  isApplying?: boolean;
};

const useStyles = makeStyles()((theme: Theme) => {
  return {
    sectionHeading: {
      marginBottom: theme.spacing(1),
      fontSize: '14px',
    },
    section: {
      padding: theme.spacing(0, 2),
      marginTop: theme.spacing(3),
    },
    commentSection: {
      padding: theme.spacing(0, 2),
      marginTop: theme.spacing(3),
      '--gds-spacing-field-container-margin': '0',
    },
    descriptionSection: {
      padding: theme.spacing(0, 2),
      marginTop: theme.spacing(1),
    },
    falsePositive: {
      padding: theme.spacing(0, 3),
      marginTop: theme.spacing(2),
    },
    readOnlyWrapper: {
      pointerEvents: 'none',
      ' textarea': {
        opacity: 0.38,
      },
    },
    statusIcon: {
      marginRight: theme.spacing(0.5),
      verticalAlign: 'text-bottom',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100px',
    },
    customBadge: {
      backgroundColor: 'transparent !important',
      overflow: 'hidden',
      '& .MuiChip-label': {
        paddingTop: theme.spacing(0.2),
        height: '100%',
        paddingLeft: theme.spacing(1),
      },
    },
    radioGroup: {
      paddingLeft: theme.spacing(1),
    },
    labelContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    unassignedLabel: {
      marginLeft: theme.spacing(3.5),
    },
    flagIcon: {
      fontSize: '14px',
      marginLeft: theme.spacing(0.5),
    },
    bulkActionDialog: {
      minHeight: '585px',
    },
    cancelText: {
      color: '#FFFFFF',
      cursor: 'pointer',
      userSelect: 'none',
      fontFamily: 'Open Sans, sans-serif',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: 0,
      marginRight: '16px',
    },
    dialogTitlePaddingOverride: {
      '& .MuiDialogTitle-root .MuiGrid-container': {
        paddingLeft: '0 !important',
      },
    },
    contentClassNameOverride: {
      overflow: 'initial',
    },
    dialogRoot: {
      zIndex: 100002,
    },
  };
});

const BulkActionModal = ({
  open,
  onClose,
  onApply,
  onApplyOverrides,
  onCancelOverrides,
  selectedCount = 0,
  overrides,
  isApplying = false,
}: BulkActionModalProps) => {
  const { classes } = useStyles();
  const tracker = useTracker();
  const inspectionId = useSelector(getInspectionId);
  const { data: inspectionCustomStatuses, isLoading: isLoadingStatuses } = useInspectionCustomStatuses(inspectionId);

  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isFalsePositive, setIsFalsePositive] = useState(false);
  const [overrideStatusChoice, setOverrideStatusChoice] = useState('apply-new');
  const [overrideCommentChoice, setOverrideCommentChoice] = useState('apply-new');

  const showOverrideModal = overrides?.overrideStatus || overrides?.overrideComment;

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedStatus(value === selectedStatus ? null : value);
  };

  const handleCommentChange = (value: string | null) => {
    setComment(value ?? '');
  };

  const handleFalsePositiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setIsFalsePositive(isChecked);

    if (isChecked) {
      setComment(FALSE_POSITIVE_COMMENT);
    } else {
      setComment('');
    }
  };

  const handleApply = () => {
    const selectedStatusDetails = activeStatuses.find((status) => status.id === selectedStatus);
    const params = {
      statusId: selectedStatus === null ? '' : selectedStatus,
      comment,
      isFalsePositive,
      selectedStatusTitle: selectedStatusDetails?.title,
    };

    if (showOverrideModal && onApplyOverrides) {
      onApplyOverrides(params, {
        overrideStatus: overrideStatusChoice === 'override-all',
        overrideComment: overrideCommentChoice === 'override-all',
      });
    } else {
      onApply(params);
    }
  };

  const resetForm = () => {
    setSelectedStatus(null);
    setComment('');
    setIsFalsePositive(false);
    setOverrideStatusChoice('apply-new');
    setOverrideCommentChoice('apply-new');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCancel = () => {
    if (showOverrideModal && onCancelOverrides) {
      onCancelOverrides();
    } else {
      resetForm();
      onClose();
    }
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const activeStatuses =
    !isLoadingStatuses && inspectionCustomStatuses?.currentStatuses
      ? Object.values(inspectionCustomStatuses.currentStatuses).filter((status) => status.active)
      : [];

  const getStatusIcon = (status: CustomStatus, selected = false) => {
    const color = selected ? getStatusColor(status) : undefined;
    switch (status.icon) {
      case 'Thumbup':
        return (
          <ThumbUpAltIcon
            className={classes.statusIcon}
            style={{
              fontSize: 14,
              color: color || undefined,
            }}
          />
        );
      case 'Thumbdown':
        return (
          <ThumbDownOffAltIcon
            className={classes.statusIcon}
            style={{
              fontSize: 14,
              color: color || undefined,
            }}
          />
        );
      case 'Hidden':
        return (
          <VisibilityOffOutlinedIcon
            className={classes.statusIcon}
            style={{
              fontSize: 14,
              color: color || undefined,
            }}
          />
        );
      default:
        return null;
    }
  };

  const getStatusColor = (status: CustomStatus) => {
    switch (status.color) {
      case 'Green':
        return '#19CAD7';
      case 'Red':
        return '#EA3632';
      case 'Blue':
        return '#5586F1';
      default:
        return '';
    }
  };

  const getChipBackgroundColor = (status: CustomStatus) => {
    switch (status.color) {
      case 'Green':
        return '#2E5C5F';
      case 'Red':
        return '#6B3433';
      case 'Blue':
        return '#2B68ED40';
      default:
        return '';
    }
  };

  const renderOverrideForm = () => {
    return (
      <Grid
        container
        direction="column"
        sx={{ opacity: isApplying ? 0.5 : 1, pointerEvents: isApplying ? 'none' : 'auto' }}
      >
        <Grid item className={classes.descriptionSection}>
          <GVTypography variant="body2">
            Some of your selected results already have values. Choose how you want to apply changes.
          </GVTypography>
        </Grid>

        {overrides?.overrideStatus && (
          <Grid item className={classes.section}>
            <GVTypography variant="subtitle2" className={classes.sectionHeading}>
              Status
            </GVTypography>
            <RadioGroup
              value={overrideStatusChoice}
              onChange={(e) => setOverrideStatusChoice(e.target.value)}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="apply-new"
                control={<GVRadio color="secondary" disabled={isApplying} />}
                label="Apply only to results without a status"
                disabled={isApplying}
              />
              <FormControlLabel
                value="override-all"
                control={<GVRadio color="secondary" disabled={isApplying} />}
                label="Override all statuses"
                disabled={isApplying}
              />
            </RadioGroup>
          </Grid>
        )}

        {overrides?.overrideComment && (
          <Grid item className={classes.section}>
            <GVTypography variant="subtitle2" className={classes.sectionHeading}>
              Comment
            </GVTypography>
            <RadioGroup
              value={overrideCommentChoice}
              onChange={(e) => setOverrideCommentChoice(e.target.value)}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="apply-new"
                control={<GVRadio color="secondary" disabled={isApplying} />}
                label="Apply only to results without a comment"
                disabled={isApplying}
              />
              <FormControlLabel
                value="override-all"
                control={<GVRadio color="secondary" disabled={isApplying} />}
                label="Override all comments"
                disabled={isApplying}
              />
            </RadioGroup>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderBulkActionForm = () => {
    if (isLoadingStatuses) {
      return (
        <Grid container className={classes.loadingContainer}>
          <CircularProgress size={32} />
        </Grid>
      );
    }

    return (
      <Grid
        container
        direction="column"
        sx={{ opacity: isApplying ? 0.5 : 1, pointerEvents: isApplying ? 'none' : 'auto' }}
      >
        <Grid item className={classes.descriptionSection}>
          <GVTypography variant="body2">
            Choose a status for all selected items. You can also leave a comment or flag them as false positives.
          </GVTypography>
        </Grid>

        <Grid item className={classes.section}>
          <GVTypography variant="subtitle2" className={classes.sectionHeading}>
            Status
          </GVTypography>

          <RadioGroup
            value={selectedStatus === null ? '' : selectedStatus}
            onChange={handleStatusChange}
            className={classes.radioGroup}
          >
            {activeStatuses.map((status) => {
              return (
                <FormControlLabel
                  key={status.id}
                  value={status.id}
                  disabled={isApplying}
                  control={
                    <GVRadio
                      color="secondary"
                      disabled={isApplying}
                      onClick={(e) => {
                        if (isApplying) return;
                        if (selectedStatus === status.id) {
                          e.preventDefault();
                          setSelectedStatus(null);
                        }
                      }}
                    />
                  }
                  label={
                    <GVChip
                      className={classes.customBadge}
                      sx={{
                        '& .MuiChip-label': {
                          backgroundColor: getChipBackgroundColor(status),
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                      label={
                        <div>
                          {getStatusIcon(status, true)} {status.title}
                        </div>
                      }
                    />
                  }
                />
              );
            })}
            <FormControlLabel
              value="unassigned"
              disabled={isApplying}
              control={
                <GVRadio
                  color="secondary"
                  disabled={isApplying}
                  onClick={(e) => {
                    if (isApplying) return;
                    if (selectedStatus === 'unassigned') {
                      e.preventDefault();
                      setSelectedStatus(null);
                    }
                  }}
                />
              }
              label={<span>Mark as unassigned (remove status)</span>}
            />
          </RadioGroup>
        </Grid>

        <Grid item className={classes.falsePositive}>
          <FormControlLabel
            style={{ marginLeft: '2px' }}
            disabled={isApplying}
            control={
              <GVCheckbox checked={isFalsePositive} onChange={handleFalsePositiveChange} disabled={isApplying} />
            }
            label={
              <Grid container item wrap={'nowrap'} alignItems={'center'} width={'100%'}>
                Report false positive
                <OutlinedFlagIcon className={classes.flagIcon} />
              </Grid>
            }
            sx={{
              '& .MuiFormControlLabel-label': {
                marginLeft: '-18px',
              },
            }}
          />
        </Grid>

        <Grid item className={classes.commentSection}>
          <GVTypography variant="subtitle2" className={classes.sectionHeading}>
            Comment
          </GVTypography>

          <div className={isFalsePositive || isApplying ? classes.readOnlyWrapper : undefined}>
            <GVTextField
              label="Comment"
              isLabelHidden
              placeholder="Optional—explain your decision or leave a note for collaborators."
              value={comment}
              onValueChange={handleCommentChange}
              multiline
              rows={6}
              isDisabled={isApplying}
            />
          </div>
        </Grid>
      </Grid>
    );
  };

  const getApplyButtonLabel = () => {
    if (isApplying) {
      return (
        <>
          <CircularProgress size={16} color="inherit" sx={{ marginRight: 1 }} />
          Applying to Selected
        </>
      );
    }
    return showOverrideModal ? 'Apply Changes' : `Apply to ${selectedCount} selected`;
  };

  const actionButtons = (
    <Grid container justifyContent="flex-end" gap={1} alignItems="center">
      <Typography
        component="span"
        className={classes.cancelText}
        data-testid="cancelEdit"
        onClick={isApplying ? undefined : handleCancel}
        sx={{ opacity: isApplying ? 0.5 : 1, cursor: isApplying ? 'default' : 'pointer' }}
      >
        Cancel
      </Typography>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleApply}
        disabled={(comment === '' && selectedStatus === null) || isApplying}
        sx={{ fontWeight: 600 }}
      >
        {getApplyButtonLabel()}
      </Button>
    </Grid>
  );

  const modalProps = {
    title: showOverrideModal ? 'Heads up — existing values detected' : 'Bulk Review',
    open,
    callToActionText: '',
    children: showOverrideModal ? renderOverrideForm() : renderBulkActionForm(),
    hideCallToAction: true,
    handleCallToActionClick: () => {},
    handleCloseModal: handleClose,
    additionalActions: actionButtons,
    contentClassName: classes.contentClassNameOverride,
    classes: {
      paper: showOverrideModal ? '' : classes.bulkActionDialog,
      root: `${classes.dialogTitlePaddingOverride} ${classes.dialogRoot}`,
    },
    disableClose: isApplying,
  };

  return <VerifyDialog {...modalProps} />;
};

export default BulkActionModal;
